import React, { FC, PointerEvent, useEffect, useRef, useState, WheelEvent } from 'react';
import { BasicButton } from 'src/components/widgets/buttons';
import { Dialog } from 'src/components/widgets/dialog';
import { Icon } from 'src/components/widgets/icon';
import { ZTooltip } from 'src/components/widgets/ztooltip';
import ChevronLeftIcon from 'src/shared/material_icons/navigation/chevron_left.svg';
import ChevronRightIcon from 'src/shared/material_icons/navigation/chevron_right.svg';
import ZoomInIcon from 'src/shared/material_icons/action/zoom_in.svg';
import ZoomOutIcon from 'src/shared/material_icons/action/zoom_out.svg';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const ZOOM_STEP = 1.25;

const Viewport = styled.div<{ $zoomed: boolean; $dragging: boolean }>`
    position: relative;
    display: flex;
    height: 70vh;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 12px;
    background: #f5f7fa;
    cursor: ${(props) => (props.$dragging ? 'grabbing' : props.$zoomed ? 'grab' : 'zoom-in')};
    touch-action: none;
    user-select: none;
`;

// The stage hugs the image so the print-area boxes, positioned as a percentage of it, line up exactly.
// The transform changes on every wheel step and pointer move: set through attrs so no new class is generated each time.
const ZoomStage = styled.div.attrs<{ $zoom: number; $x: number; $y: number }>((props) => ({
    style: { transform: `translate(${props.$x}px, ${props.$y}px) scale(${props.$zoom})` }
}))<{ $zoom: number; $x: number; $y: number }>`
    position: relative;
    display: inline-block;
    max-width: 100%;
    max-height: 100%;
    transform-origin: center;
`;

const StageImage = styled.img`
    display: block;
    max-width: 100%;
    max-height: 70vh;
    pointer-events: none;
`;

// A print area drawn over the zoomed image, in the same teal as the preview.
const AreaBox = styled.div`
    position: absolute;
    box-sizing: border-box;
    border: 2px dashed #00899d;
    background: rgba(0, 137, 157, 0.08);
    pointer-events: none;
`;

const AreaTag = styled.span`
    position: absolute;
    top: 0;
    left: 0;
    max-width: 100%;
    padding: 1px 5px;
    overflow: hidden;
    background: #00899d;
    color: white;
    font-size: 10px;
    line-height: 1.4;
    white-space: nowrap;
    text-overflow: ellipsis;
`;

const Controls = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 12px;
    color: #455a64;
    font-size: 12px;
`;

const ChipRows = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 12px;
`;

const ChipRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Chip = styled.button<{ $selected: boolean }>`
    height: 28px;
    padding: 0 12px;
    border: 1px solid ${(props) => (props.$selected ? props.theme.app.primary_dark : props.theme.app.borderColor)};
    border-radius: 999px;
    background: ${(props) => (props.$selected ? props.theme.app.primary_dark : 'white')};
    color: ${(props) => (props.$selected ? 'white' : props.theme.app.textColor)};
    font-family: inherit;
    font-size: 12px;
    cursor: pointer;

    &:hover {
        border-color: ${(props) => props.theme.app.primary_dark};
    }
`;

// Previous and next side, at the left and right ends of the image area.
const SideArrow = styled.button<{ $side: 'left' | 'right' }>`
    position: absolute;
    top: 50%;
    ${(props) => props.$side}: 16px;
    z-index: 2;
    display: grid;
    width: 40px;
    height: 40px;
    place-items: center;
    padding: 0;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 50%;
    background: white;
    box-shadow: 0 2px 8px rgba(38, 50, 56, 0.16);
    color: #455a64;
    cursor: pointer;
    transform: translateY(-50%);

    ${Icon}, ${Icon} svg {
        width: 22px;
        height: 22px;
        min-width: 22px;
        fill: currentColor;
    }

    &:hover {
        color: #263238;
        border-color: #b0bec5;
    }

    &:focus-visible {
        outline: 2px solid #1769aa;
        outline-offset: 1px;
    }
`;

const ZoomValue = styled.span`
    min-width: 44px;
    text-align: center;
`;

export interface AgentZoomArea {
    /** Position and size as a fraction (0–1) of the image. */
    left: number;
    top: number;
    width: number;
    height: number;
    label?: string;
}

export interface AgentZoomImage {
    url: string;
    name: string;
    /** Print areas to draw over the image, so the zoom shows where designs go. */
    areas?: AgentZoomArea[];
}

export interface AgentZoomVariant {
    name: string;
    /** The side images of the variant. */
    images: AgentZoomImage[];
}

interface AgentImageZoomDialogProps {
    /** Variants with at least one side image; the dialog switches between variants and between their sides. */
    variants: AgentZoomVariant[];
    /** The image to open on. */
    initialUrl: string;
    onClose: () => void;
}

/** Full size view of an artifact image: wheel or buttons to zoom, drag to move around the zoomed image. */
export const AgentImageZoomDialog: FC<AgentImageZoomDialogProps> = ({ variants, initialUrl, onClose }) => {
    const [variantIndex, setVariantIndex] = useState(() =>
        Math.max(
            0,
            variants.findIndex((variant) => variant.images.some((image) => image.url === initialUrl))
        )
    );
    const images = variants[variantIndex].images;
    const [index, setIndex] = useState(() =>
        Math.max(
            0,
            images.findIndex((image) => image.url === initialUrl)
        )
    );
    const currentIndex = Math.min(index, images.length - 1);
    const { url, name: sideName } = images[currentIndex];
    const title = variants.length > 1 ? `${variants[variantIndex].name} · ${sideName}` : sideName;
    const hasManyImages = images.length > 1;
    const [zoom, setZoom] = useState(MIN_ZOOM);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const dragRef = useRef<{ pointerId: number; x: number; y: number; moved: boolean } | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const applyZoom = (next: number) => {
        const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
        setZoom(clamped);
        if (clamped === MIN_ZOOM) setOffset({ x: 0, y: 0 });
    };

    // Each side opens unzoomed and centered.
    const showImage = (next: number) => {
        setIndex((next + images.length) % images.length);
        setZoom(MIN_ZOOM);
        setOffset({ x: 0, y: 0 });
    };

    // Another variant opens on the same side when it has one with that name, otherwise on its first side.
    const showVariant = (next: number) => {
        const sameSide = variants[next].images.findIndex((image) => image.name === sideName);
        setVariantIndex(next);
        setIndex(Math.max(0, sameSide));
        setZoom(MIN_ZOOM);
        setOffset({ x: 0, y: 0 });
    };

    useEffect(() => {
        if (!hasManyImages) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') showImage(currentIndex - 1);
            if (event.key === 'ArrowRight') showImage(currentIndex + 1);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasManyImages, index, variantIndex]);

    const onWheel = (event: WheelEvent<HTMLDivElement>) => applyZoom(event.deltaY < 0 ? zoom * ZOOM_STEP : zoom / ZOOM_STEP);

    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;
        dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId || zoom === MIN_ZOOM) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        dragRef.current = { ...drag, x: event.clientX, y: event.clientY, moved: true };
        setIsDragging(true);
        setOffset((current) => ({ x: current.x + dx, y: current.y + dy }));
    };

    const onPointerUp = () => {
        // A click without dragging zooms in, like the zoom-in cursor promises.
        if (dragRef.current && !dragRef.current.moved && zoom === MIN_ZOOM) applyZoom(zoom * 2);
        dragRef.current = null;
        setIsDragging(false);
    };

    return (
        <Dialog title={title} size='large' showCloseButton closeOnOverlayClick onClose={onClose}>
            {(variants.length > 1 || hasManyImages) && (
                <ChipRows>
                    {variants.length > 1 && (
                        <ChipRow role='tablist' aria-label={T._('Variants', 'Admin')}>
                            {variants.map((variant, itemIndex) => (
                                <Chip
                                    key={itemIndex}
                                    type='button'
                                    role='tab'
                                    aria-selected={itemIndex === variantIndex}
                                    $selected={itemIndex === variantIndex}
                                    onClick={() => showVariant(itemIndex)}
                                >
                                    {variant.name}
                                </Chip>
                            ))}
                        </ChipRow>
                    )}
                    {hasManyImages && (
                        <ChipRow role='tablist' aria-label={T._('Sides', 'Admin')}>
                            {images.map((image, itemIndex) => (
                                <Chip
                                    key={image.url}
                                    type='button'
                                    role='tab'
                                    aria-selected={itemIndex === currentIndex}
                                    $selected={itemIndex === currentIndex}
                                    onClick={() => showImage(itemIndex)}
                                >
                                    {image.name}
                                </Chip>
                            ))}
                        </ChipRow>
                    )}
                </ChipRows>
            )}
            <Viewport
                $zoomed={zoom > MIN_ZOOM}
                $dragging={isDragging}
                onWheel={onWheel}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
            >
                <ZoomStage $zoom={zoom} $x={offset.x} $y={offset.y}>
                    <StageImage src={url} alt={title} draggable={false} />
                    {(images[currentIndex].areas ?? []).map((area, areaIndex) => (
                        <AreaBox
                            key={areaIndex}
                            style={{
                                left: `${area.left * 100}%`,
                                top: `${area.top * 100}%`,
                                width: `${area.width * 100}%`,
                                height: `${area.height * 100}%`
                            }}
                        >
                            {area.label && <AreaTag>{area.label}</AreaTag>}
                        </AreaBox>
                    ))}
                </ZoomStage>
                {hasManyImages &&
                    (
                        [
                            { side: 'left', label: T._('Previous side', 'Admin'), step: -1, icon: <ChevronLeftIcon /> },
                            { side: 'right', label: T._('Next side', 'Admin'), step: 1, icon: <ChevronRightIcon /> }
                        ] as const
                    ).map((arrow) => (
                        <SideArrow
                            key={arrow.side}
                            type='button'
                            $side={arrow.side}
                            aria-label={arrow.label}
                            // The arrows sit on the image: pressing them must not start a drag or a click-to-zoom.
                            onPointerDown={(event) => event.stopPropagation()}
                            onPointerUp={(event) => event.stopPropagation()}
                            onClick={() => showImage(currentIndex + arrow.step)}
                        >
                            <Icon>{arrow.icon}</Icon>
                        </SideArrow>
                    ))}
            </Viewport>
            <Controls>
                <ZTooltip position='top' title={T._('Zoom out', 'Admin')}>
                    <BasicButton
                        type='button'
                        $size='small'
                        $type='icon'
                        aria-label={T._('Zoom out', 'Admin')}
                        disabled={zoom <= MIN_ZOOM}
                        onClick={() => applyZoom(zoom / ZOOM_STEP)}
                    >
                        <Icon>
                            <ZoomOutIcon />
                        </Icon>
                    </BasicButton>
                </ZTooltip>
                <ZoomValue>{Math.round(zoom * 100)}%</ZoomValue>
                <ZTooltip position='top' title={T._('Zoom in', 'Admin')}>
                    <BasicButton
                        type='button'
                        $size='small'
                        $type='icon'
                        aria-label={T._('Zoom in', 'Admin')}
                        disabled={zoom >= MAX_ZOOM}
                        onClick={() => applyZoom(zoom * ZOOM_STEP)}
                    >
                        <Icon>
                            <ZoomInIcon />
                        </Icon>
                    </BasicButton>
                </ZTooltip>
                <BasicButton type='button' $size='small' disabled={zoom === MIN_ZOOM} onClick={() => applyZoom(MIN_ZOOM)}>
                    {T._('Reset', 'Admin')}
                </BasicButton>
            </Controls>
        </Dialog>
    );
};
